/* eslint-disable */
import { Button, Flex, List, Select } from "antd";
import { ReactElement, useEffect, useState } from "react";
import { InfinityLineField, InlineField } from "./AccountInfo/AccountInfoCard";
import { InboxOutlined, SearchOutlined } from "@ant-design/icons";
import { useStore } from "../../../stores/root-store-context";
import * as React from "react";
import { dateValidation } from "../helpers/dateValidation";
import ResponsiveGrid from "Components/TableMui/ResponsiveGrid";
import { PaginationConfig } from "antd/es/pagination";

type ArchitecturalNote = {
    architecturalNoteKey: string;
    created: string;
    architecturalKey: string;
    employeeKey: number;
    note: string;
    entered: string;
    fileName: string | null;
    imageBase64: string | null;
    enteredStr: string;
    fileUrl: string | null;
};

export type ArchitecturalData = {
    ArchitecturalKey: string;
    a_Created: string;
    UnitKey: string;
    EmployeeKey: string;
    a_Received: string;
    a_Status: string;
    a_Modification: string;
    a_Condition: string;
    a_Closed: string;
    a_Changed: boolean;
    UnitAddress: string;
    ResidentName: string;
    employeeFirstName: string;
    employeeLastName: string;
    fileUrl: string | null;
    fileBase64: string | null;
    isFile: boolean;
    receivedStr: string;
    closedStr: string;
    architecturalNotes: ArchitecturalNote[];
};

const styles = {
    architecturalCard: {
        flex: '1 1 260px',
        minWidth: 0,
        textAlign: 'start' as const,
        alignSelf: 'stretch',
    }
}

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const ArchitecturalNotesGrid: React.FC<{ notes: ArchitecturalNote[] }> = ({ notes }) => {
    const { modalViewerStore } = useStore();
    const cardId = (React as any).useId();

    const architecturalNotes = React.useMemo(
        () => (notes ?? []).map((el, index) => ({ ...el, key: index })),
        [notes]
    );
   
    const [pagination, setPagination] = React.useState<PaginationConfig>({
        current: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: architecturalNotes.length,
    });

    const handlePageChange = (current: number, pageSize: number) => {
        setPagination((prev) => ({ ...prev, current, pageSize }));
    };

    const pagedNotes = React.useMemo(() => {
        const pageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;
        const current = pagination.current || 1;
        const start = (current - 1) * pageSize;
        return architecturalNotes.slice(start, start + pageSize);
    }, [architecturalNotes, pagination]);

    const columns = [
        {
            label: "Created Date",
            field: "created",
            flex: 0.3,
            minWidth: 120,
            sortable: false,
            render: (date: string) => dateValidation(date),
        },
        {
            label: "Note",
            field: "note",
            flex: 1.2,
            minWidth: 200,
            primary: true,
            sortable: false,
        },
        {
            label: "",
            field: "action",
            flex: 0.1,
            minWidth: 90,
            sortable: false,
            render: (_: any, row: any) => {
                return row.fileUrl ? <Button data-testid={`ric-architectural-view-btn-${cardId}`} icon={<SearchOutlined />} onClick={() => {
                    const parts = row.fileUrl.split("/");
                    const fileName = parts[parts.length - 1];
                    modalViewerStore.openFileByUrl(row.fileUrl, fileName)
                }}>View</Button> : <></>;
            },
        },
    ];

    return (
        <ResponsiveGrid
            columns={columns}
            rows={pagedNotes}
            getRowId={(row) => row.key}
            emptyText={"No data."}
            emptyIcon={<InboxOutlined />}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            server={{
                page: pagination.current || 1,
                pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
                total: architecturalNotes.length,
                onChange: handlePageChange,
                
            }}
        />
    );
};

const Architectural = (props: { architecturalData: ArchitecturalData[], }): ReactElement => {
    const { architecturalData } = props;
    const uniqueStatusesArray = Array.from(new Set(["All", ...architecturalData.map(item => item.a_Status).filter(el => el?.trim().length > 0)]));
    const sortedStatuses = uniqueStatusesArray.sort((a, b) => a.localeCompare(b));
    const [selectFilter, setSelectFileter] = useState<string>("All");
    const [filteredNotes, setFilteredNotes] = useState<ArchitecturalData[]>(architecturalData);

    useEffect(() => {
        if (selectFilter === "All") {
            setFilteredNotes(architecturalData);
        } else {
            setFilteredNotes(architecturalData.filter(item => item.a_Status === selectFilter))
        }
    }, [architecturalData, selectFilter])

    return (
        <List
            dataSource={filteredNotes}
            header={<Select
                defaultValue="All"
                style={{ width: "100%", maxWidth: 300, marginBottom: 10 }}
                onChange={(value: string) => { setSelectFileter(value) }}
                options={sortedStatuses.map(el => { return { value: el, label: el } })}
            />}
            renderItem={(item, index) => (
                <div style={{ borderBottom: "1px solid lightgray", paddingBottom: 10, paddingTop: 10 }}>
                    <Flex wrap="wrap" gap="small">
                        <div style={styles.architecturalCard}>
                            <InlineField label="Received:" value={item.receivedStr} />
                            <InlineField label="Modification:" value={item.a_Modification} />
                            <InlineField label="Status:" value={item.a_Status} />
                        </div>
                        <div style={styles.architecturalCard}>
                            <InlineField label="Employee:" value={item.employeeFirstName} />
                        </div>
                        <div style={styles.architecturalCard}>
                            <InlineField label="Closed:" value={item.a_Closed && item.a_Closed?.split("T")[0] !== "0001-01-01" ? item.closedStr : ""} />
                        </div>
                        <div style={styles.architecturalCard}>
                            <InlineField label="Closed by:" value={
                                item.a_Closed && item.a_Closed?.split("T")[0] !== "0001-01-01"
                                    ? item.employeeLastName + ", " + item.employeeFirstName
                                    : ""
                            } />
                        </div>
                        <Flex vertical flex={"1 1 100%"} gap="small" style={{ minWidth: 0 }}>
                            <InfinityLineField label="Condition:" value={item.a_Condition != null || item.a_Condition !== undefined ? item.a_Condition : ""} />
                            <b>Notes:</b>
                            {item.architecturalNotes && item.architecturalNotes.length > 0
                                ? <div style={{ flex: "1 1", overflow: "hidden" }}>
                                    <ArchitecturalNotesGrid notes={item.architecturalNotes} />
                                </div>
                                : null}
                        </Flex>
                    </Flex>

                </div>
            )}
        />

    )
}

export default Architectural;